/**
 * Files travel in chunks: each chunk of up to 4 MiB is encrypted on its own,
 * so a 100 MB file is read, encrypted, sent, received and decrypted piece by
 * piece; the sender never holds the whole encrypted file at once.
 *
 * This is the cutting and the putting back together. Sealing a chunk, and what binds it to its file and position, is
 * `@/crypto` (`fileChunks.ts`).
 */
import type { EncryptedFile, Progress } from '@/api/types';

import { CHUNK_OVERHEAD, ChunkSealer, DecryptError, encryptFileHeader, newFileNonce, openChunk, sanitizeFileName, type FileHeader, type MessageBinding } from '@/crypto';

/** Plaintext bytes per chunk; the server accepts chunks up to this size plus the 19 bytes of head and tag. */
const FILE_CHUNK_BYTES = 4 * 1024 * 1024;
/** The most plaintext a chunk may hold, whatever the sender's header says: the server's limit. */
export const MAX_CHUNK_BYTES = FILE_CHUNK_BYTES;
export { CHUNK_OVERHEAD } from '@/crypto';

export function chunkCount(size: number, chunkBytes: number): number {
  return Math.max(1, Math.ceil(size / chunkBytes));
}

/** A picked file; `File` fits, and so does anything else with a name, a type and slices. */
export interface FileSource {
  readonly name: string;
  readonly type: string;
  readonly size: number;
  slice(start: number, end: number): Blob;
}

/** Prepares a file for sending: encrypts the header now (bound to its message) and each chunk when the transport asks for it. */
export async function encryptFile(key: CryptoKey, file: FileSource, binding: MessageBinding, chunkBytes = FILE_CHUNK_BYTES): Promise<EncryptedFile> {
  const nonce = newFileNonce();
  const header: FileHeader = { name: sanitizeFileName(file.name), type: file.type, size: file.size, chunkBytes, nonce };
  const count = chunkCount(file.size, chunkBytes);
  const sealer = new ChunkSealer(key, nonce, count);
  return {
    id: binding.messageId,
    header: await encryptFileHeader(key, header, binding),
    chunks: count,
    chunkBytes,
    size: file.size + count * CHUNK_OVERHEAD,
    async chunk(index: number) {
      if (!Number.isInteger(index) || index < 0 || index >= count) throw new RangeError(`No chunk ${String(index)}`);
      const start = index * chunkBytes;
      const plain = new Uint8Array(await file.slice(start, Math.min(start + chunkBytes, file.size)).arrayBuffer());
      return sealer.seal(index, plain);
    },
  };
}

/**
 * Fetches and decrypts a file chunk by chunk. `chunks` is what the server
 * says the message has; it must match the layout in the encrypted header.
 */
export async function decryptFile(
  key: CryptoKey,
  header: FileHeader,
  chunks: number,
  fetchChunk: (index: number) => Promise<Uint8Array<ArrayBuffer>>,
  onProgress?: Progress,
): Promise<Blob> {
  const count = chunkCount(header.size, header.chunkBytes);
  if (count !== chunks) throw new DecryptError('The file is incomplete.');
  const parts: Uint8Array<ArrayBuffer>[] = [];
  let received = 0;
  for (let index = 0; index < count; index += 1) {
    const plain = await openChunk(key, await fetchChunk(index), header.nonce, index, count);
    received += plain.length;
    parts.push(plain);
    onProgress?.((index + 1) / count);
  }
  if (received !== header.size) throw new DecryptError('The file is incomplete.');
  return new Blob(parts, { type: header.type || 'application/octet-stream' });
}
